import { Accordion as MantineAccordion, AccordionProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type AccordionVariant = 'default' | 'contained' | 'filled' | 'separated' | 'gradient';

export interface ThemedAccordionProps extends Omit<AccordionProps, 'variant'> {
  accordionVariant?: AccordionVariant;
  gradientVariant?: GradientVariant;
}

interface AccordionComponent extends React.ForwardRefExoticComponent<ThemedAccordionProps & React.RefAttributes<HTMLDivElement>> {
  Item: typeof MantineAccordion.Item;
  Control: typeof MantineAccordion.Control;
  Panel: typeof MantineAccordion.Panel;
}

const BaseAccordion = MantineAccordion as any;

const Accordion = forwardRef<HTMLDivElement, ThemedAccordionProps>(
  ({ accordionVariant = 'default', gradientVariant = 'primary', styles, children, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];

    const variantMap: Record<AccordionVariant, AccordionProps['variant']> = {
      default: 'default',
      contained: 'contained',
      filled: 'filled',
      separated: 'separated',
      gradient: 'filled',
    };

    if (accordionVariant !== 'gradient') {
      return (
        <BaseAccordion ref={ref} variant={variantMap[accordionVariant]} styles={styles} {...rest}>
          {children}
        </BaseAccordion>
      );
    }

    return (
      <BaseAccordion
        ref={ref}
        variant="filled"
        styles={{
          ...styles,
          item: {
            '&[data-active]': {
              backgroundImage: `linear-gradient(135deg, ${gradient.from}15, ${gradient.to}15)`,
              borderColor: gradient.from,
            },
            ...(styles as any)?.item,
          },
          control: {
            '&[data-active]': {
              color: gradient.from,
            },
            ...(styles as any)?.control,
          },
          chevron: {
            '&[data-rotate]': {
              color: gradient.from,
            },
            ...(styles as any)?.chevron,
          },
        }}
        {...rest}
      >
        {children}
      </BaseAccordion>
    );
  }
) as AccordionComponent;

Accordion.displayName = 'Accordion';

// Attach subcomponents
Accordion.Item = MantineAccordion.Item;
Accordion.Control = MantineAccordion.Control;
Accordion.Panel = MantineAccordion.Panel;

export default Accordion;
